import React from 'react';
import { Image, Spin } from 'antd';
import { PlayCircleOutlined, PictureOutlined, AudioOutlined } from '@ant-design/icons';
import { isImageType, isVideoType, isMixedType, isAudioType } from './utils';

const FALLBACK_SVG =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDY0IDY0IiBmaWxsPSIjY2NjIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNlZWUiLz48dGV4dCB4PSIzMiIgeT0iMzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5OTkiPuWcluacquaJvuWIsDwvdGV4dD48L3N2Zz4=';

/**
 * 渲染訊息中的圖片或影片媒體內容。
 * 若無媒體類型則回傳 null。
 */
const ChatMediaContent = ({ msg }) => {
  const { messageType, caption, content } = msg;
  const mediaUrls = msg.mediaUrls || msg.MediaUrls || null;
  const videoUrls = msg.videoUrls || msg.VideoUrls || null;
  const mediaUrl =
    msg.mediaUrl ||
    msg.MediaUrl ||
    (mediaUrls && mediaUrls.length === 1 ? mediaUrls[0] : null);

  const effectiveMt =
    messageType ||
    (content === '[image]' ? 'image' : null) ||
    (content === '[video]' ? 'video' : null);

  if (!effectiveMt) return null;

  // ── audio：語音訊息 + STT 轉文字 ───────────────────────────────────────────
  if (isAudioType(effectiveMt)) {
    // content 持有 STT 轉文字（成功）或 "[audio]"（失敗/未設定）
    const sttText = msg.sttText ||
      (content && content !== '[audio]' && !/^\[.*\]$/.test(content) ? content : null);
    const audioUrl = msg.mediaUrl || msg.MediaUrl || null;

    return (
      <div style={{ minWidth: 180, maxWidth: 260 }}>
        {/* 語音播放器 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(0,0,0,0.04)',
            borderRadius: 8,
            padding: '6px 10px'
          }}
        >
          <AudioOutlined style={{ fontSize: 18, color: '#25d366', flexShrink: 0 }} />
          {audioUrl ? (
            <audio
              controls
              src={audioUrl}
              style={{ flex: 1, height: 32, minWidth: 0 }}
              preload="metadata"
            />
          ) : (
            <span style={{ fontSize: 12, color: '#888', flex: 1 }}>語音訊息</span>
          )}
        </div>
        {/* STT 轉文字結果 */}
        {sttText ? (
          <div
            style={{
              marginTop: 6,
              padding: '4px 8px',
              background: 'rgba(22,119,255,0.06)',
              borderRadius: 6,
              borderLeft: '2px solid #1677ff',
              fontSize: 12,
              color: '#444',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.5
            }}
          >
            <span style={{ fontSize: 10, color: '#1677ff', display: 'block', marginBottom: 2 }}>
              STT 轉文字
            </span>
            {sttText}
          </div>
        ) : (
          <div style={{ marginTop: 4, fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>
            （未轉文字）
          </div>
        )}
      </div>
    );
  }

  // ── mixed_media_batch：圖片網格 + 影片列 在同一氣泡 ────────────────────────
  if (isMixedType(effectiveMt)) {
    const imgList = mediaUrls && mediaUrls.length > 0 ? mediaUrls : mediaUrl ? [mediaUrl] : [];
    const vidList = videoUrls && videoUrls.length > 0 ? videoUrls : [];
    const labelText = caption ||
      (content && !/^\[.*\]$/.test(content) ? content : null);

    const imgCols = imgList.length <= 2 ? imgList.length : imgList.length <= 4 ? 2 : 3;

    return (
      <div>
        {/* 圖片網格 */}
        {imgList.length > 0 && (
          <Image.PreviewGroup>
            <div
              style={imgList.length > 1 ? {
                display: 'grid',
                gridTemplateColumns: `repeat(${imgCols}, 1fr)`,
                gap: 4,
                maxWidth: 280,
                marginBottom: vidList.length > 0 ? 6 : 0
              } : { marginBottom: vidList.length > 0 ? 6 : 0 }}
            >
              {imgList.map((url, idx) => (
                <div key={idx} style={{ overflow: 'hidden', borderRadius: 6, background: '#f0f0f0' }}>
                  <Image
                    src={url}
                    alt={`image-${idx + 1}`}
                    width={imgList.length > 1 ? '100%' : 240}
                    style={imgList.length > 1
                      ? { width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }
                      : { maxWidth: 240, maxHeight: 200, borderRadius: 8, objectFit: 'cover', display: 'block' }}
                    placeholder={<div style={{ width: '100%', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}><Spin size="small" /></div>}
                    fallback={FALLBACK_SVG}
                  />
                </div>
              ))}
            </div>
          </Image.PreviewGroup>
        )}
        {/* 影片列 */}
        {vidList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {vidList.map((url, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <video src={url} controls style={{ maxWidth: 280, maxHeight: 180, borderRadius: 8, display: 'block' }} />
                {vidList.length > 1 && (
                  <div style={{ position: 'absolute', top: 6, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 10, fontSize: 11, padding: '1px 7px', pointerEvents: 'none' }}>
                    {idx + 1}/{vidList.length}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {labelText && (
          <div style={{ marginTop: 4, fontSize: 13, color: '#555', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{labelText}</div>
        )}
      </div>
    );
  }

  const imageList = isImageType(effectiveMt)
    ? mediaUrls && mediaUrls.length > 0
      ? mediaUrls
      : mediaUrl
      ? [mediaUrl]
      : []
    : [];

  if (isImageType(effectiveMt) && imageList.length > 0) {
    const labelText =
      caption ||
      (content &&
      !/^\[image\]$/.test(content) &&
      !/^\[\d+\s*張圖片\]$/.test(content)
        ? content
        : null);

    const isMulti = imageList.length > 1;
    const cols =
      imageList.length <= 2 ? imageList.length : imageList.length <= 4 ? 2 : 3;
    const thumbSize = isMulti ? '100%' : 240;

    return (
      <div style={{ position: 'relative' }}>
        <Image.PreviewGroup>
          <div
            style={
              isMulti
                ? {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                    gap: 4,
                    maxWidth: 280
                  }
                : {}
            }
          >
            {imageList.map((url, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 6,
                  background: '#f0f0f0'
                }}
              >
                <Image
                  src={url}
                  alt={`image-${idx + 1}`}
                  width={thumbSize}
                  style={
                    isMulti
                      ? {
                          width: '100%',
                          aspectRatio: '1 / 1',
                          objectFit: 'cover',
                          display: 'block'
                        }
                      : {
                          maxWidth: 240,
                          maxHeight: 200,
                          borderRadius: 8,
                          objectFit: 'cover',
                          display: 'block'
                        }
                  }
                  placeholder={
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f0f0f0'
                      }}
                    >
                      <Spin size="small" />
                    </div>
                  }
                  fallback={FALLBACK_SVG}
                />
                {isMulti && idx === 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 4,
                      bottom: 4,
                      background: 'rgba(0,0,0,0.65)',
                      color: '#fff',
                      borderRadius: 10,
                      fontSize: 11,
                      padding: '1px 8px',
                      lineHeight: '16px',
                      pointerEvents: 'none',
                      fontWeight: 500
                    }}
                  >
                    {imageList.length}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
        {labelText && (
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: '#555',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {labelText}
          </div>
        )}
        {isMulti && !labelText && (
          <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
            {imageList.length} 張圖片
          </div>
        )}
      </div>
    );
  }

  if (isVideoType(effectiveMt)) {
    // 收集所有影片 URL（video_batch 用 mediaUrls，單一 video 用 mediaUrl）
    const videoList =
      mediaUrls && mediaUrls.length > 0
        ? mediaUrls
        : mediaUrl
        ? [mediaUrl]
        : [];

    if (videoList.length > 0) {
      const isMulti = videoList.length > 1;
      const labelText =
        caption ||
        (content && !/^\[\d+\s*段影片\]$/.test(content) && !/^\[影片\]$/.test(content)
          ? content
          : null);

      return (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {videoList.map((url, idx) => (
              <div key={idx} style={{ position: 'relative' }}>
                <video
                  src={url}
                  controls
                  style={{ maxWidth: 280, maxHeight: 200, borderRadius: 8, display: 'block' }}
                />
                {isMulti && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 8,
                      background: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      borderRadius: 10,
                      fontSize: 11,
                      padding: '1px 7px',
                      pointerEvents: 'none'
                    }}
                  >
                    {idx + 1}/{videoList.length}
                  </div>
                )}
              </div>
            ))}
          </div>
          {labelText && (
            <div style={{ marginTop: 4, fontSize: 13, color: '#555', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {labelText}
            </div>
          )}
          {isMulti && !labelText && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
              {videoList.length} 段影片
            </div>
          )}
        </div>
      );
    }

    // 無 URL（影片 metadata 僅告知）
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: 13 }}>
        <PlayCircleOutlined style={{ fontSize: 18 }} />
        <span>{caption || content || '[影片訊息]'}</span>
      </div>
    );
  }

  if (isImageType(effectiveMt)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: 13 }}>
        <PictureOutlined style={{ fontSize: 18 }} />
        <span>{caption || '[圖片訊息]'}</span>
      </div>
    );
  }

  return null;
};

export default ChatMediaContent;
